import { useState, useEffect, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetLookUpDropdownListQuery } from '../../../../../api/endpoints/lookup';
import { useGetUserAccountsQuery, useCreateUserAccountMutation, useUpdateUserAccountMutation, useDeleteUserAccountMutation } from '../../../../../api/endpoints/userAccount';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const DEFAULT_PAGE_SIZE = 10;
const ACTIVE_RECORDS = 1;
const PASSIVE_RECORDS = 0;

const INITIAL_FILTERS = {
    firstName: '',
    lastName: '',
    email: '',
    dealerName: '',
    activeOnly: null,
    country: ''
};

const mapSortOrderToDir = (order) => {
    if (order === 1) return 'ASC';
    if (order === -1) return 'DESC';
    return null;
};

export const useUserAccountsData = ({ dealerAddressId } = {}) => {
    const { getCurrentLanguageId } = useLanguage();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [serverFilters, setServerFilters] = useState(INITIAL_FILTERS);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [sortConfig, setSortConfig] = useState({
        field: null,
        order: null
    });

    const [originalFormData, setOriginalFormData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [createUserAccount] = useCreateUserAccountMutation();
    const [updateUserAccount] = useUpdateUserAccountMutation();
    const [deleteUserAccount] = useDeleteUserAccountMutation();

    const { data: usageTypeOptions = [] } = useGetLookUpDropdownListQuery({
        GroupCode: 'XXBOS_USAGE_TYPE',
        LanguageId: getCurrentLanguageId()
    });

    const getActiveOnlyValue = (activeOnlyFilter) => {
        if (activeOnlyFilter === null || activeOnlyFilter === undefined) {
            return null;
        }
        return activeOnlyFilter ? ACTIVE_RECORDS : PASSIVE_RECORDS;
    };
    const lang = Number(getCurrentLanguageId?.());

    const {
        data: userAccountsData,
        isLoading,
        error,
        refetch
    } = useGetUserAccountsQuery({
        pageNumber: currentPage,
        pageSize: pageSize,
        languageId: lang === 1 || lang === 2 ? lang : 1,
        firstName: serverFilters.firstName,
        lastName: serverFilters.lastName,
        email: serverFilters.email,
        dealerName: serverFilters.dealerName,
        activeOnly: getActiveOnlyValue(serverFilters.activeOnly),
        country: serverFilters.country || null,
        sortBy: sortConfig.field ?? null,
        sortDir: mapSortOrderToDir(sortConfig.order),
        ...(Number.isFinite(Number(dealerAddressId)) && Number(dealerAddressId) > 0 ? { dealerAddressId: Number(dealerAddressId) } : {})
    });

    const rawUsers = useMemo(() => {
        return userAccountsData?.data?.items || [];
    }, [userAccountsData]);

    const totalCount = useMemo(() => {
        return userAccountsData?.data?.totalCount || 0;
    }, [userAccountsData]);

    const globalFilteredUsers = useMemo(() => {
        if (!globalFilterValue.trim()) return rawUsers;

        const filterValue = globalFilterValue.toLowerCase().trim();
        return rawUsers.filter(
            (user) => user.firstName?.toLowerCase().includes(filterValue) || user.lastName?.toLowerCase().includes(filterValue) || user.email?.toLowerCase().includes(filterValue) || user.dealerName?.toLowerCase().includes(filterValue)
        );
    }, [rawUsers, globalFilterValue]);

    const users = globalFilteredUsers;

    const filters = useMemo(
        () => ({
            global: { value: globalFilterValue, matchMode: FilterMatchMode.CONTAINS }
        }),
        [globalFilterValue]
    );

    const lazyParams = useMemo(
        () => ({
            first: (currentPage - 1) * pageSize,
            rows: pageSize,
            page: currentPage,
            sortField: sortConfig.field,
            sortOrder: sortConfig.order
        }),
        [currentPage, pageSize, sortConfig]
    );

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    const trimString = (str) => {
        return str ? str.toString().trim() : '';
    };

    const hasFormDataChanged = (currentFormData) => {
        if (!originalFormData || !currentFormData) return false;

        const fieldsToCompare = ['firstName', 'lastName', 'email', 'dealerId', 'addrId', 'usageType', 'identityNo', 'languageId', 'isActive', 'isEmailEnabled'];

        return fieldsToCompare.some((field) => {
            let originalValue = originalFormData[field];
            let currentValue = currentFormData[field];

            if (field === 'dealerId') {
                originalValue = typeof originalValue === 'object' ? originalValue?.id : originalValue;
                currentValue = typeof currentValue === 'object' ? currentValue?.id : currentValue;
            }

            if (typeof originalValue === 'string') originalValue = trimString(originalValue);
            if (typeof currentValue === 'string') currentValue = trimString(currentValue);

            return originalValue !== currentValue;
        });
    };

    const setFormOriginalData = (formData) => {
        setOriginalFormData(formData ? { ...formData } : null);
    };

    const onGlobalFilterChange = (e) => {
        const value = trimString(e.target.value);
        setGlobalFilterValue(value);
    };

    const handleAdvancedFilterChange = (updatedFilters) => {
        const apiFilters = {
            firstName: trimString(updatedFilters.firstName || ''),
            lastName: trimString(updatedFilters.lastName || ''),
            email: trimString(updatedFilters.email || ''),
            dealerName: trimString(updatedFilters.dealerName || ''),
            activeOnly: updatedFilters.activeOnly !== undefined ? updatedFilters.activeOnly : null,
            country: trimString(updatedFilters.country || '')
        };

        setServerFilters(apiFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setServerFilters(INITIAL_FILTERS);
        setGlobalFilterValue('');
        setSortConfig({ field: null, order: null });
        setCurrentPage(1);
    };

    const onPage = (event) => {
        const newPage = event.page + 1;
        setCurrentPage(newPage);

        if (event.rows !== pageSize) {
            setPageSize(event.rows);
            setCurrentPage(1);
        }
    };

    const onSort = (event) => {
        setSortConfig({
            field: event.sortField,
            order: event.sortOrder
        });
    };

    const refreshData = async () => {
        try {
            setLoading(true);
            await refetch();
        } catch (error) {
            console.error('Error refreshing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (formData) => {
        try {
            const trimmedEmail = trimString(formData.email);
            const trimmedFirstName = trimString(formData.firstName);
            const trimmedLastName = trimString(formData.lastName);

            if (!trimmedEmail || !trimmedFirstName || !trimmedLastName) {
                throw new Error('Missing required fields: email, firstName, or lastName');
            }

            const cleanFormData = {
                ...formData,
                email: trimmedEmail,
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                identityNo: formData.identityNo ? Number(trimString(formData.identityNo)) : 0
            };

            let response;

            if (cleanFormData.id) {
                const updateData = {
                    id: cleanFormData.id,
                    userId: cleanFormData.userId,
                    firstName: cleanFormData.firstName,
                    lastName: cleanFormData.lastName,
                    email: cleanFormData.email,
                    role: cleanFormData.role || 'admin',
                    dealerId: cleanFormData.dealerId || 0,
                    addrId: cleanFormData.addrId || 0,
                    usageType: cleanFormData.usageType || 0,
                    identityNo: cleanFormData.identityNo,
                    languageId: cleanFormData.languageId || 2,
                    isActive: cleanFormData.isActive !== undefined ? cleanFormData.isActive : 1,
                    isEmailEnabled: cleanFormData.isEmailEnabled !== undefined ? cleanFormData.isEmailEnabled : true,
                    country: cleanFormData.country || '',
                    phoneNumber: cleanFormData.phoneNumber || ''
                };

                if (cleanFormData.password && cleanFormData.passwordConfirmed) {
                    updateData.password = cleanFormData.password;
                    updateData.passwordConfirmed = cleanFormData.passwordConfirmed;
                }

                response = await updateUserAccount(updateData).unwrap();
            } else {
                response = await createUserAccount({
                    firstName: cleanFormData.firstName,
                    lastName: cleanFormData.lastName,
                    email: cleanFormData.email,
                    password: cleanFormData.password,
                    passwordConfirmed: cleanFormData.passwordConfirmed,
                    role: '',
                    dealerId: cleanFormData.dealerId || 0,
                    addrId: cleanFormData.addrId || 0,
                    usageType: cleanFormData.usageType || 0,
                    identityNo: cleanFormData.identityNo,
                    languageId: cleanFormData.languageId || 2,
                    isEmailEnabled: cleanFormData.isEmailEnabled !== undefined ? cleanFormData.isEmailEnabled : true,
                    country: cleanFormData.country || '',
                    phoneNumber: cleanFormData.phoneNumber || ''
                }).unwrap();
            }

            if (response?.isSuccessful) {
                setOriginalFormData(null);
                await refreshData();
                return { success: true, data: response };
            } else {
                throw new Error(response?.message || 'Failed to save user account');
            }
        } catch (error) {
            console.error('Error saving user account:', error);
            throw error;
        }
    };

    const handleRemove = async (selectedUser) => {
        try {
            if (!selectedUser?.id) {
                throw new Error('User ID is required for deletion');
            }

            const response = await deleteUserAccount(selectedUser.id).unwrap();

            if (response?.isSuccessful) {
                setOriginalFormData(null);
                await refreshData();
                return { success: true, data: response };
            } else {
                throw new Error(response?.message || 'Failed to delete user account');
            }
        } catch (error) {
            console.error('Error deleting user account:', error);
            throw error;
        }
    };

    return {
        users,
        allUsers: users,
        filteredUsers: users,
        loading,
        filters,
        globalFilterValue,

        totalCount,
        lazyParams,
        error,
        pageSize,

        activeFilters: serverFilters,

        onGlobalFilterChange,
        onPage,
        onSort,

        handleAdvancedFilterChange,
        handleResetFilters,

        usageTypeOptions,
        handleSave,
        handleRemove,
        refetch: refreshData,

        trimString,
        hasFormDataChanged,
        setFormOriginalData
    };
};
